import { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, Outlet, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Center } from '@astryxdesign/core/Center';
import { Spinner } from '@astryxdesign/core/Spinner';
import { isLocale } from '@qronstat/i18n';
import { api, onUnauthorized, setCsrfToken } from '../api/client';
import { sessionKey, useSession } from '../api/hooks';
import type { SessionUser } from '../api/hooks';
import { AuthLayout } from '../auth/AuthLayout';
import { ChangePasswordForm } from '../auth/ChangePasswordForm';
import { AppContextProvider } from './context';
import { useDeviceCache } from './deviceCache';
import { ExportProvider } from './exports';
import { RealtimeProvider } from './realtime';
import { usePreferences } from './preferences';
import { Shell } from './Shell';
import { TelegramPrompt } from '../components/telegram/TelegramPrompt';
import { startSilentRefresh } from './silent';

function useServerPreferences(user: SessionUser) {
  const prefs = usePreferences();
  const synced = useRef<string | null>(null);
  useEffect(() => {
    if (synced.current === user.id) return;
    synced.current = user.id;
    if (isLocale(user.locale) && user.locale !== prefs.locale) prefs.setLocale(user.locale);
    if (user.theme !== prefs.themeMode) prefs.setThemeMode(user.theme);
    if (user.timeMode !== prefs.timeMode) prefs.setTimeMode(user.timeMode);
  }, [user, prefs]);
}

function ActiveApp({ user }: { user: SessionUser }) {
  const intl = useIntl();
  const client = useQueryClient();
  const cache = useDeviceCache();
  const navigate = useNavigate();
  useServerPreferences(user);
  useEffect(() => {
    void cache.attach(user.id);
  }, [cache, user.id]);
  useEffect(
    () =>
      onUnauthorized(() => {
        setCsrfToken(null);
        void cache.clear().then(() => {
          client.setQueryData(sessionKey, { authenticated: false });
          void navigate({ to: '/login' });
        });
      }),
    [cache, client, navigate],
  );
  const minutes = useRef(user.silentRefreshMinutes);
  minutes.current = user.silentRefreshMinutes;
  useEffect(
    () => startSilentRefresh(client, () => Math.min(10, Math.max(5, minutes.current || 5))),
    [client],
  );
  if (user.mustChangePassword) {
    return (
      <AuthLayout
        title={intl.formatMessage({ id: 'auth.mustChange.title' })}
        subtitle={intl.formatMessage({ id: 'auth.mustChange.subtitle' })}
      >
        <ChangePasswordForm onDone={() => void client.invalidateQueries({ queryKey: sessionKey })} />
      </AuthLayout>
    );
  }
  return (
    <AppContextProvider user={user}>
      <RealtimeProvider>
        <ExportProvider>
          <Shell>
            <Outlet />
            <TelegramPrompt />
          </Shell>
        </ExportProvider>
      </RealtimeProvider>
    </AppContextProvider>
  );
}

export function AppGate() {
  const session = useSession();
  const data = session.data;
  useEffect(() => {
    if (data?.csrfToken) setCsrfToken(data.csrfToken);
  }, [data]);
  if (!data) {
    if (session.isError) return <Navigate to="/login" replace />;
    return (
      <Center height="100dvh">
        <Spinner size="lg" />
      </Center>
    );
  }
  if (!data.authenticated) {
    if (data.stage === 'setup') return <Navigate to="/login/setup" replace />;
    if (data.stage === 'password') return <Navigate to="/login/code" replace />;
    return <Navigate to="/login" replace />;
  }
  return <ActiveApp user={data.user} />;
}

export async function patchMe(
  patch: Partial<Pick<SessionUser, 'locale' | 'theme' | 'timeMode' | 'calmMap' | 'silentRefreshMinutes'>>,
): Promise<void> {
  await api('/api/me', { method: 'PATCH', body: patch });
}
